'use client';

import { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
  useFileUpload,
  useSwrScreeningItems,
  useSSEScreeningItems,
  mapScreeningItemToCvFile,
} from '@hooks/useSwrApi/endpoints/screening';
import type { ApiSchema } from '@type/api/apiUtilityFn';
import { useTranslation } from 'react-i18next';
import { FileUploadZone } from '../FileUploadZone';
import { CandidateCard } from '../CandidateCard';
import { CandidateProfileModal } from '../CandidateProfileModal';
import { CandidateStatsFilter } from '../CandidateStatsFilter';
import type { CvFile, FilterType, MatchStats } from '../screening.types';

interface ProjectDetailViewProps {
  project: ApiSchema<'ScreeningHistoryItemDto'>;
  onBack: () => void;
}

export const ProjectDetailView = ({ project, onBack }: ProjectDetailViewProps) => {
  const [cvFiles, setCvFiles] = useState<CvFile[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<CvFile | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [hasItemsOrUploading, setHasItemsOrUploading] = useState(false);
  const { t } = useTranslation();

  // Get the screening job slug from project
  const jobSlug = project.jobSlug ?? null;

  // Sync items array to cvFiles state
  const syncItems = useCallback((newItems: ApiSchema<'ScreeningItemDto'>[]) => {
    if (newItems && newItems.length > 0) {
      setCvFiles(newItems.map((item) => mapScreeningItemToCvFile(item)));
      setHasItemsOrUploading(true);
    }
  }, []);

  // Initial data load via SWR (one-time fetch, no polling)
  const {
    items: initialItems,
    stats: initialStats,
    jobId: jobIdFromItems,
    mutate: refreshItems,
  } = useSwrScreeningItems({
    jobSlug,
    refreshInterval: 0,
    enabled: !!jobSlug,
  });

  // Use jobId from API response, fallback to project.jobId
  const jobId = jobIdFromItems ?? project.jobId;

  // Sync initial SWR data to cvFiles and detect if we have items
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setHasItemsOrUploading(true);
    }
    syncItems(initialItems);
  }, [initialItems, syncItems]);

  // File upload hook
  const {
    isDragActive, handleDrag, handleDrop, handleFileInput, isUploading,
  } = useFileUpload({
    cvFiles,
    setCvFiles,
    jobSlug,
    onUploadStart: () => {
      // Enable SSE when upload starts
      setHasItemsOrUploading(true);
    },
    onUploadAccepted: () => {
      void refreshItems();
    },
  });

  // SSE for real-time updates - only enabled when there are items or upload is in progress
  // This prevents infinite reconnections for empty projects:
  // - Backend keeps SSE open for 10 minutes for empty projects (allItemsFinished returns false for empty arrays)
  // - We only connect when we know there are/will be items to process
  // - Connection is closed immediately when receiving empty items array
  const {
    items: sseItems,
    stats: sseStats,
    isProcessing,
    completedCount,
    totalCount,
  } = useSSEScreeningItems({
    jobSlug,
    enabled: !!jobSlug && (hasItemsOrUploading || isUploading),
    onItemsUpdate: (updatedItems) => syncItems(updatedItems),
    onProcessingComplete: () => {
      void refreshItems();
    },
  });

  // Use SSE stats when available, fall back to initial SWR stats
  const backendStats = sseStats ?? initialStats;

  // Sync SSE items to cvFiles when SSE has data
  useEffect(() => {
    syncItems(sseItems);
  }, [sseItems, syncItems]);


  const hasResults = completedCount > 0;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Sort files by score (highest first)
  const sortedFiles = [...cvFiles].sort((a, b) => (b.score || 0) - (a.score || 0));

  // Check if we have any matched candidates (candidates with scores/matchLevel)
  const hasMatchedCandidates = sortedFiles.some((file) => file.matchLevel || file.score !== undefined);

  // Use backend stats or calculate from frontend as fallback
  const matchStats: MatchStats = backendStats
    ? {
      excellent: backendStats.excellentMatches ?? 0,
      good: backendStats.goodMatches ?? 0,
      fair: backendStats.fairMatches ?? 0,
      poor: backendStats.poorMatches ?? 0,
    }
    : sortedFiles.reduce(
      (stats, file) => {
        if (file.matchLevel) {
          stats[file.matchLevel] += 1;
        }
        return stats;
      },
      {
        excellent: 0, good: 0, fair: 0, poor: 0,
      } as MatchStats,
    );

  // Filter files based on active filter
  const filteredFiles = activeFilter === 'all'
    ? sortedFiles
    : sortedFiles.filter((file) => file.matchLevel === activeFilter);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="z-10 flex h-16 shrink-0 items-center gap-4 border-b border-input bg-surface/50 px-8 backdrop-blur-sm">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 font-medium text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
        >
          <ArrowLeftIcon className="size-5" />
          {t('global.action.back')}
        </button>
        <div className="h-6 w-px bg-slate-200" />
        <h1 className="truncate text-xl font-bold">
          {project.jobSummary?.title || t('screening.title.empty')}
        </h1>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="ml-auto flex items-center gap-2 rounded-full bg-secondary/10 px-3 py-1.5">
            <div className="size-2 animate-pulse rounded-full bg-secondary" />
            <span className="text-sm font-medium text-secondary">
              {t('screening.processing.label')}
              {' '}
              {completedCount}
              /
              {totalCount}
            </span>
          </div>
        )}
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Upload Zone */}
        {!hasResults ? (
          <FileUploadZone
            isDragActive={isDragActive}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onFileInput={handleFileInput}
          />
        ) : (
          <FileUploadZone
            isDragActive={isDragActive}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onFileInput={handleFileInput}
            compact
          />
        )}

        {/* Processing Progress */}
        {isProcessing && totalCount > 0 && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {t('screening.processing.cvs.label')}
              </span>
              <span className="text-sm font-bold text-secondary">
                {Math.round(progress)}
                %
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-secondary to-secondary/80 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Candidate Stats & Filter */}
        {hasMatchedCandidates && (
          <CandidateStatsFilter
            stats={matchStats}
            totalCount={sortedFiles.length}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />
        )}

        {/* Candidate Cards Grid */}
        {filteredFiles.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredFiles.map((file) => (
              <CandidateCard
                key={file.id}
                file={file}
                isExpanded={expandedId === file.id}
                onToggle={() => {
                  // Open modal when clicking a completed card
                  if (file.status === 'completed') {
                    setSelectedCandidate(file);
                  } else {
                    setExpandedId(expandedId === file.id ? null : file.id);
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {cvFiles.length === 0 && !isProcessing && (
          <div className="mt-8 text-center">
            <p className="font-medium text-slate-400">
              {t('screening.no.candidates.uploaded')}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              {t('screening.drag.drop')}
            </p>
          </div>
        )}
      </div>

      {/* Candidate Profile Modal */}
      <CandidateProfileModal
        isOpen={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        file={selectedCandidate}
        jobId={jobId}
        onStatusChanged={() => {
          // Refresh items when status changes
          void refreshItems();
        }}
      />
    </div>
  );
};

export default ProjectDetailView;
